"use client";

import React, { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import ReCAPTCHA from "react-google-recaptcha";
import {
  generateServiceFromPathname,
  shouldShowServiceDropdown,
  REQUIRED_SERVICE,
} from "@/types/constants";
import { QuoteForm } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { renderRichText } from "@/lib/richText";
import Image from 'next/image';

// freemail detection
const FREEMAIL_RE = /@(gmail|googlemail|yahoo|hotmail|outlook|aol|icloud|protonmail|gmx|live|mail|yandex|zoho|msn|inbox|fastmail)(\.[a-z]{2,})?$/i;
function isFreemail(email?: string) { return Boolean(email && FREEMAIL_RE.test(email)); }

interface FormError {
  field: string;
  message: string;
}

interface QuoteFormData {
  name: string;
  email_address: string;
  company: string;
  phone_number: string;
  service: string | null;
  how_did_you_find_us: string;
  enquiry: string;
  marketing: boolean;
  analytics_id: string;
  "WC GCLID": string;
}

interface QuoteFormProps {
  blok?: QuoteForm & SbBlokData;
}

interface SubmissionResponse {
  success: boolean;
  message?: string;
  errors?: FormError[];
  recaptcha?: {
    success: boolean;
    challenge_ts?: string;
    hostname?: string;
    "error-codes"?: string[];
  };
  debug?: {
    tokenPreview: string;
    secretKeyPrefix: string;
  };
}

export default function QuoteFormComponent({ blok }: QuoteFormProps) {
  const hasRequiredFields = () => {
    if (!formData.name?.trim()) return false;
    if (!formData.email_address?.trim()) return false;
    if (!formData.company?.trim()) return false;
    if (!formData.enquiry?.trim()) return false;
    if (showServiceDropdown && (!formData.service || formData.service === "")) return false;
    return true;
  };
  const canSubmit = () => !isSubmitting && Boolean(recaptchaToken) && hasRequiredFields();

  const [formData, setFormData] = useState<QuoteFormData>({
    name: "",
    email_address: "",
    company: "",
    phone_number: "",
    service: null,
    how_did_you_find_us: "",
    enquiry: "",
    marketing: false,
    analytics_id: "GTM-PHZ7KSZ",
    "WC GCLID": "",
  });

  const [errors, setErrors] = useState<FormError[]>([]);
  const [submissionError, setSubmissionError] = useState<string>("");
  const shouldShowSubmissionError = Boolean(submissionError && !errors.some(err => err.message === submissionError));
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const pathname = usePathname();
  const currentService = generateServiceFromPathname(pathname);
  const showServiceDropdown = shouldShowServiceDropdown(pathname);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [csrfToken, setCsrfToken] = useState<string>('');

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      service: currentService,
    }));
  }, [currentService]);

  useEffect(() => {
    fetch('/api/csrf')
      .then(res => {
        const token = res.headers.get('x-csrf-token');
        if (token) {
          setCsrfToken(token);
        }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const getGclidParam = () => {
      if (typeof window === 'undefined') return null;
      const match = RegExp('[?&]gclid=([^&]*)').exec(window.location.search);
      return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : null;
    };

    const shouldStoreGclid = () => {
      const gclsrc = new URLSearchParams(window.location.search).get('gclsrc');
      return !gclsrc || gclsrc.indexOf('aw') !== -1;
    };

    const gclidParam = getGclidParam();

    if (gclidParam && shouldStoreGclid()) {
      const expiryDate = new Date().getTime() + (90 * 24 * 60 * 60 * 1000);
      const gclidData = {
        value: gclidParam,
        expiryDate: expiryDate
      };
      localStorage.setItem('gclid', JSON.stringify(gclidData));

      setFormData(prev => ({
        ...prev,
        "WC GCLID": gclidParam
      }));
    } else {
      const storedGclid = localStorage.getItem('gclid');
      if (storedGclid) {
        const gclidData = JSON.parse(storedGclid);
        if (new Date().getTime() < gclidData.expiryDate) {
          setFormData(prev => ({
            ...prev,
            "WC GCLID": gclidData.value
          }));
        } else {
          localStorage.removeItem('gclid');
        }
      }
    }
  }, []);

  if (!blok) return null;

  const {
    image,
    quote_type,
    title = "Get a fast quote",
    description = "Our expert consultants will get back to you as soon as possible.",
    left_title,
    left_subtitle,
    left_content,
    button_label,
  } = blok;

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const inputValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: inputValue,
    }));
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[QuoteForm] handleSubmit called');
    setErrors([]);
    setSubmissionError("");

    try {
      if (isFreemail(formData.email_address)) {
        const msg = 'Business emails only. Please use a company email address.';
        setErrors([{ field: 'email_address', message: msg }]);
        setSubmissionError(msg);
        return;
      }
    } catch { /* ignore */ }

    // Read token directly from the DOM — same source Zapier reads
    const currentToken = document.querySelector<HTMLTextAreaElement>('textarea[name="g-recaptcha-response"]')?.value
      || recaptchaRef.current?.getValue()
      || recaptchaToken;
    if (!currentToken) {
      setSubmissionError("Please complete the reCAPTCHA verification.");
      return;
    }
    setIsSubmitting(true);

    // Get browser info
    const userAgent = navigator.userAgent;

    // Prepare form data
    const submissionData = {
      ...formData,
      user_agent: userAgent,
      service: formData.service || "Penetration Testing",
    };

    console.log("[QuoteForm] Submitting form data:", submissionData);
    console.log('[QuoteForm] recaptchaToken length:', currentToken.length);

    try {
      const response = await fetch("/api/quote", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          ...submissionData,
          form_name: "fast-quote",
          form_url: window.location.href,
          recaptchaToken: currentToken,
        }),
      });

      console.log("submissionData:", JSON.stringify(submissionData, null, 2));

      // For debugging
      console.log("Response status:", response.status);

      // Check if response is JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Response was not JSON");
      }

      const data: SubmissionResponse = await response.json();
      console.log("[QuoteForm] Response data:", data);
      console.log('[reCAPTCHA] siteverify response:', data.recaptcha ?? 'not included in response');
      console.log('[reCAPTCHA] server debug:', data.debug ?? 'not included in response');

      if (!response.ok) {
        console.error("[QuoteForm] Submission failed:", {
          status: response.status,
          message: data.message,
          errors: data.errors,
          recaptcha: data.recaptcha,
        });
        if (data.errors) {
          setErrors(data.errors);
        } else {
          setSubmissionError(
            data.message || "An error occurred while submitting the form."
          );
        }
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
        return;
      }

      // Clear form on success
      setFormData({
        name: "",
        email_address: "",
        company: "",
        phone_number: "",
        service: "",
        how_did_you_find_us: "",
        enquiry: "",
        marketing: false,
        analytics_id: "GTM-PHZ7KSZ",
        "WC GCLID": "",
      });

      recaptchaRef.current?.reset();
      setRecaptchaToken(null);

      // Show success message
      alert("Thank you! Your quote request has been submitted successfully.");
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmissionError(
        "An error occurred while submitting the form. Please try again later."
      );
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const preSubmitCheck = (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
      const testFlag = typeof process !== 'undefined' && process.env.NEXT_PUBLIC_TEST_FREEMAIL === '1';
      const isTargetHost = host.includes('targetdefense') || (testFlag && host.includes('localhost'));
      if (isTargetHost && isFreemail(formData.email_address)) {
        e.preventDefault();
        e.stopPropagation();
        const msg = 'Business emails only. Please use a company email address.';
        setErrors([{ field: 'email_address', message: msg }]);
        setSubmissionError(msg);
        return false;
      }
    } catch { /* ignore */ }
    return true;
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="fast-quote">
      <div className="row gy-2 mb-3">
        <div className="form-row">
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="form-input"
            placeholder="Your name *"
            aria-label="Your name"
            required
          />
        </div>
        <div className="form-row">
          <input
            type="email"
            name="email_address"
            value={formData.email_address}
            onChange={handleInputChange}
            className="form-input"
            placeholder="Email address *"
            aria-label="Email address"
            required
          />
        </div>
        <div className="form-row">
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            className="form-input"
            placeholder="Company name *"
            aria-label="Company name"
            required
          />
        </div>
        <div className="form-row">
          <input
            type="tel"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleInputChange}
            className="form-input"
            placeholder="Telephone"
            aria-label="Telephone"
          />
        </div>
        {showServiceDropdown ? (
          <>
            <div className="form-row">
              <div className="full-width">
                <div className="styled-select">
                  <select
                    name="service"
                    value={formData.service || ""}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    aria-label="Required service"
                    aria-required="true"
                    aria-invalid={formData.service === ''}
                  >
                    <option value="">Please select required service *</option>
                    {REQUIRED_SERVICE.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="full-width">
                <input
                  type="text"
                  name="how_did_you_find_us"
                  value={formData.how_did_you_find_us}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="How did you hear about us?"
                  aria-label="How did you hear about us"
                />
              </div>
            </div>
          </>
        ) : (
          <div className="d-none">
            <input
              type="hidden"
              name="service"
              value={formData.service || ""}
              aria-label="service"
            />
          </div>
        )}
        <div className="form-row">
          <textarea
            name="enquiry"
            value={formData.enquiry}
            onChange={handleInputChange}
            rows={3}
            maxLength={1500}
            className="form-input"
            placeholder="Comments *"
            required
            aria-label="enquiry"
          />
        </div>
        <div className="form-row">
          <p className="mb-0">
            <label className="small text-dark">
              <input
                type="checkbox"
                name="marketing"
                checked={formData.marketing}
                onChange={handleInputChange}
                className="me-2"
                aria-label="marketing"
              />
              I agree to receive sales and marketing communication. For more
              information about how we collect, process and retain your personal
              data, please see our{" "}
              <Link href="/privacy-notice" target="_blank">
                Privacy policy
              </Link>
            </label>
          </p>
        </div>
      </div>
      <div>
        <input
          type="hidden"
          name="analytics_id"
          value={formData.analytics_id}
          className="analytics_id"
          aria-label="analytics_id"
        />
        <input
          type="hidden"
          id="gclid_field"
          name="WC GCLID"
          value={formData["WC GCLID"]}
        />
        <div className="text-center">
          <div className="d-flex justify-content-center mb-3">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
              onChange={handleRecaptchaChange}
            />
          </div>
          <button
            type="submit"
            className="button button-large button-secondary"
            disabled={!canSubmit()}
            onClick={preSubmitCheck}
            style={!canSubmit() ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
          >
            {isSubmitting ? "Submitting..." : button_label || "Get my quote"}
          </button>
        </div>
      </div>
    </form>
  );

  if (quote_type === "quote_blue") {
    return (
      <div
        {...storyblokEditable(blok)}
        className="dark-highlight-section"
        id="getQuote"
      >
        <div className="container">
          <div className="padding-small text-center">
            <h2>{title || "Get a fast pen test quote"}</h2>
            <p className="larger-text">
              {description ||
                "One of our expert pen test consultants will get back to you as soon as possible."}
            </p>

            <form className="form-quote" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="one-half">
                  <label htmlFor="name">Name *</label>
                  <input
                    className="form-input"
                    required
                    type="text"
                    name="name"
                    id="quote_name"
                    value={formData.name}
                    onChange={handleInputChange}
                    aria-label="name"
                  />
                </div>
                <div className="one-half">
                  <label htmlFor="phone_number">Phone number</label>
                  <input
                    className="form-input"
                    type="tel"
                    name="phone_number"
                    id="quote_phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    aria-label="phone number"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="one-half">
                  <label htmlFor="company">Company Name *</label>
                  <input
                    className="form-input"
                    required
                    type="text"
                    name="company"
                    aria-label="company"
                    id="quote_company"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="one-half">
                  <label htmlFor="email_address">Email address *</label>
                  <input
                    className="form-input"
                    required
                    type="email"
                    name="email_address"
                    aria-label="email address"
                    id="quote_email_address"
                    value={formData.email_address}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {showServiceDropdown ? (
                <>
                  <div className="form-row">
                    <div className="full-width">
                      <div className="styled-select">
                        <select
                          name="service"
                          value={formData.service || ""}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          aria-label="Required service"
                          aria-required="true"
                          aria-invalid={formData.service === ''}
                        >
                          <option value="">
                            Please select required service *
                          </option>
                          {REQUIRED_SERVICE.map((service) => (
                            <option key={service} value={service}>
                              {service}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="full-width">
                      <input
                        type="text"
                        name="how_did_you_find_us"
                        value={formData.how_did_you_find_us}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="How did you hear about us?"
                        aria-label="How did you hear about us?"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="d-none">
                  <input
                    type="hidden"
                    name="service"
                    value={formData.service || ""}
                    aria-label="service"
                  />
                </div>
              )}

              <div className="form-row">
                <div className="full-width">
                  <label htmlFor="enquiry">Information on your inquiry *</label>
                  <textarea
                    rows={4}
                    className="form-input"
                    required
                    name="enquiry"
                    id="quote_enquiry"
                    value={formData.enquiry}
                    onChange={handleInputChange}
                    aria-label="enquiry"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="full-width marketing-opt-in">
                  <p>
                    <label>
                      <input
                        type="checkbox"
                        name="marketing"
                        id="quote_marketing"
                        checked={formData.marketing}
                        onChange={handleInputChange}
                        aria-label="marketing"
                      />
                      I agree to receive sales and marketing communication
                    </label>
                  </p>
                  <p>
                    For more information about how we collect, process and
                    retain your personal data, please see our{" "}
                    <Link href="/privacy-notice" target="_blank">
                      privacy policy
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <div className="form-row">
                <div className="full-width">
                  <div className="text-center">
                    <input
                      type="hidden"
                      id="gclid_field"
                      name="WC GCLID"
                      value={formData["WC GCLID"]}
                    />
                    <div className="d-flex justify-content-center mb-3">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
                        onChange={handleRecaptchaChange}
                      />
                    </div>
                    <button
                      type="submit"
                      className="button button-large button-primary"
                      disabled={!canSubmit()}
                      onClick={preSubmitCheck}
                      style={!canSubmit() ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                    >
                      {isSubmitting ? "Submitting..." : button_label || "Submit"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (quote_type === "get_a_fast") {
    return (
      <div
        {...storyblokEditable(blok)}
        className="brand-section py-4 py-md-5 quote_form_target"
      >
        <div className="container my-2 my-md-3" id="getQuote">
          <div className="card card-lighter">
            <div className="row">
              <div className="col-lg-12 bg-dark p-4 d-flex flex-column justify-content-center">
                <div className="m-2">
                  <h2 className="text-primary text-center text-lg-start">
                    {left_title || "Get a fast penetration test quote"}
                  </h2>
                  <p className="fs-3 text-center text-lg-start mb-4 pb-2">
                    {left_subtitle}
                  </p>
                  {left_content && (
                    <div
                      className="list-targets lead mb-0"
                      dangerouslySetInnerHTML={{
                        __html: renderRichText(left_content),
                      }}
                    ></div>
                  )}
                </div>
              </div>
              <div className="col-lg-12 p-4">
                <div className="m-2">{renderForm()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (quote_type === "get_a_fast_pen_test") {
    return (
      <div {...storyblokEditable(blok)} className="brand-section">
        <div className="container py-4 py-md-5" id="getQuoteTop">
          <div className="card card-lighter">
            <div className="row">
              <div className="col-24 col-lg-12" style={{ position: 'relative' }}>
                {image?.filename && (
                  <Image
                    src={image.filename}
                    alt=""
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    loading="lazy"
                    style={{ objectFit: 'cover' }}
                  />
                )}
              </div>
              <div className="col-24 col-lg-12 p-4">
                <h2 className="h3 text-primary text-center text-lg-start">
                  {title || "Get a fast pen test quote"}
                </h2>
                <p className="text-center text-lg-start text-dark">
                  {description ||
                    "One of our expert pen test consultants will get back to you as soon as possible."}
                </p>

                <form onSubmit={handleSubmit} className="fast-quote">
                  <div className="row gy-2 mb-3 mb-md-4">
                    <div className="form-row">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Your name *"
                        id="quote2_name"
                        required
                        aria-label="Your name"
                      />
                    </div>
                    <div className="form-row">
                      <input
                        type="email"
                        name="email_address"
                        value={formData.email_address}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Email address *"
                        id="quote2_email_address"
                        required
                        aria-label="Email address"
                      />
                    </div>
                    <div className="form-row">
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Company name *"
                        id="quote2_company"
                        required
                        aria-label="Company name"
                      />
                    </div>
                    <div className="form-row">
                      <input
                        type="tel"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Telephone"
                        id="quote2_phone_number"
                        aria-label="Telephone"
                      />
                    </div>
                    <div className="d-none">
                      <select
                        name="service"
                        value={formData.service || "Penetration Testing"}
                        onChange={handleInputChange}
                        required
                        aria-label="Required service"
                        aria-required="true"
                        aria-invalid={formData.service === ''}
                      >
                        <option value="Penetration Testing">
                          Penetration Testing
                        </option>
                      </select>
                    </div>
                    <div className="form-row">
                      <textarea
                        name="enquiry"
                        value={formData.enquiry}
                        onChange={handleInputChange}
                        rows={3}
                        maxLength={1500}
                        className="form-input"
                        placeholder="Comments *"
                        id="quote2_enquiry"
                        required
                        aria-label="enquiry"
                      />
                    </div>
                    <div className="form-row">
                      <p className="mb-0">
                        <label className="small text-dark">
                          <input
                            type="checkbox"
                            name="marketing"
                            checked={formData.marketing}
                            onChange={handleInputChange}
                            className="me-2"
                            id="quote2_marketing"
                            aria-label="marketing"
                          />
                          I&apos;d like to receive free cyber and compliance
                          resources, and stay up-to-date with Target Defense
                          services.{" "}
                          <Link href="/privacy-notice" target="_blank">
                            Privacy policy
                          </Link>
                        </label>
                      </p>
                    </div>
                  </div>
                  <div>
                    <input
                      type="hidden"
                      name="analytics_id"
                      value={formData.analytics_id}
                      className="analytics_id"
                      aria-label="analytics_id"
                    />
                    <input
                      type="hidden"
                      id="gclid_field"
                      name="WC GCLID"
                      value={formData["WC GCLID"]}
                    />
                    <div className="text-center">
                      <div className="d-flex justify-content-center mb-3">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
                          onChange={handleRecaptchaChange}
                        />
                      </div>
                      <button
                        type="submit"
                        className="btn btn-md btn-secondary"
                        disabled={!canSubmit()}
                        onClick={preSubmitCheck}
                        style={!canSubmit() ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                      >
                        {isSubmitting
                          ? "Submitting..."
                          : button_label || "Get my quote"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (quote_type === "quote_contact") {
    return (
      <div className="quote-section-dark" id="getQuote">
        <div className="container">
          <div className="padding-medium text-center">
            {errors.length > 0 && (
              <div className="full-width">
                <div className="alert alert-shifted alert-error">
                  {errors.map((error, index) => (
                    <p key={index} className="mb-0">
                      {error.message}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {shouldShowSubmissionError && (
              <div className="full-width">
                <div className="alert alert-shifted alert-error">
                  <p>{submissionError}</p>
                </div>
              </div>
            )}

            <h2>{title || "Get a fast pen test quote"}</h2>
            <p className="larger-text">
              {description ||
                "One of our expert pen test consultants will get back to you as soon as possible."}
            </p>

            <form onSubmit={handleSubmit} className="fast-quote form-quote">
              {/* Name and Phone Row */}
              <div className="form-row">
                <div className="two-thirds">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    aria-label="name"
                  />
                </div>
                <div className="one-third">
                  <label htmlFor="phone_number">Phone number</label>
                  <input
                    type="tel"
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    className="form-input"
                    aria-label="phone_number"
                  />
                </div>
              </div>

              {/* Company Name Row */}
              <div className="form-row">
                <div className="full-width">
                  <label htmlFor="company">Company Name *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    aria-label="company"
                  />
                </div>
              </div>

              {/* Email Row */}
              <div className="form-row">
                <div className="full-width">
                  <label htmlFor="email_address">Email address *</label>
                  <input
                    type="email"
                    id="email_address"
                    name="email_address"
                    value={formData.email_address}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    aria-label="email_address"
                  />
                </div>
              </div>

              {/* Service Selection Row */}
              {showServiceDropdown ? (
                <>
                  <div className="form-row">
                    <div className="full-width">
                      <label htmlFor="service">Required service *</label>
                      <div className="styled-select">
                        <select
                          id="service"
                          name="service"
                          value={formData.service || ""}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          aria-label="Required service"
                          aria-required="true"
                          aria-invalid={formData.service === ''}
                        >
                          <option value="">
                            Please select required service *
                          </option>
                          {REQUIRED_SERVICE.map((service) => (
                            <option key={service} value={service}>
                              {service}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="full-width">
                      <label htmlFor="how_did_you_find_us">
                        How did you hear about us?
                      </label>
                      <input
                        type="text"
                        name="how_did_you_find_us"
                        value={formData.how_did_you_find_us}
                        onChange={handleInputChange}
                        className="form-input"
                        aria-label="how did you find us"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="d-none">
                  <input
                    type="hidden"
                    name="service"
                    value={formData.service || ""}
                    aria-label="service"
                  />
                </div>
              )}

              {/* Inquiry Row */}
              <div className="form-row">
                <div className="full-width">
                  <label htmlFor="enquiry">Information on your inquiry *</label>
                  <textarea
                    id="enquiry"
                    name="enquiry"
                    value={formData.enquiry}
                    onChange={handleInputChange}
                    rows={4}
                    className="form-input"
                    required
                    aria-label="enquiry"
                  />
                </div>
              </div>

              {/* Marketing Opt-in */}
              <div className="form-row">
                <div className="full-width marketing-opt-in">
                  <label>
                    <input
                      type="checkbox"
                      name="marketing"
                      checked={formData.marketing}
                      onChange={handleInputChange}
                      aria-label="marketing"
                    />
                    I agree to receive sales and marketing communication.
                  </label>
                  <p>
                    For more information about how we collect, process, and
                    retain your personal data, please see our{" "}
                    <Link href="/privacy-notice" target="_blank">
                      privacy policy
                    </Link>
                    .
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="form-row">
                <div className="full-width text-center">
                  <input
                    type="hidden"
                    id="gclid_field"
                    name="WC GCLID"
                    value={formData["WC GCLID"]}
                  />
                  <div className="d-flex justify-content-center mb-3">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
                      onChange={handleRecaptchaChange}
                    />
                  </div>
                  <button
                    type="submit"
                    className="button button-large button-primary"
                    disabled={!canSubmit()}
                    onClick={preSubmitCheck}
                    style={!canSubmit() ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                  >
                    {isSubmitting ? "Submitting..." : button_label || "Submit"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (quote_type === "bg_grey") {
    return (
      <div {...storyblokEditable(blok)} className="quote-section-gallery-grey">
        <div className="container">
          <div className="padding-medium">
            <div className="one-half margin-bottom-medium">
              {left_title && <h2>{left_title}</h2>}
              {left_subtitle && <p>{left_subtitle}</p>}
              {left_content && (
                <div
                  className="list-checked"
                  dangerouslySetInnerHTML={{
                    __html: renderRichText(left_content),
                  }}
                />
              )}
            </div>
            <div className="one-half">
              <div className="card card-lighter padding-all-small">
                {title && <h2>{title}</h2>}
                {description && (
                  <p className="mb-4 text-center text-lg-start">
                    {description}
                  </p>
                )}

                <form onSubmit={handleSubmit} className="new_quote">
                  <div className="form-row">
                    <div className="one-half">
                      <input
                        className="form-input"
                        placeholder="Name *"
                        required
                        type="text"
                        name="name"
                        id="quote_name"
                        value={formData.name}
                        onChange={handleInputChange}
                        aria-label="Name"
                      />
                    </div>
                    <div className="one-half">
                      <input
                        className="form-input"
                        placeholder="Phone number"
                        type="tel"
                        name="phone_number"
                        id="quote_phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        aria-label="Phone Number"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="one-half">
                      <input
                        className="form-input"
                        placeholder="Company Name *"
                        required
                        type="text"
                        name="company"
                        id="quote_company"
                        value={formData.company}
                        onChange={handleInputChange}
                        aria-label="Company Name"
                      />
                    </div>
                    <div className="one-half">
                      <input
                        className="form-input"
                        placeholder="Email address *"
                        required
                        type="email"
                        name="email_address"
                        id="quote_email_address"
                        value={formData.email_address}
                        onChange={handleInputChange}
                        aria-label="Email Address"
                      />
                    </div>
                  </div>

                  {showServiceDropdown ? (
                    <>
                      <div className="form-row">
                        <div className="full-width">
                          <div className="styled-select">
                            <select
                              name="service"
                              value={formData.service || ""}
                              onChange={handleInputChange}
                              className="form-input"
                              required
                              aria-label="Required service"
                              aria-required="true"
                              aria-invalid={formData.service === ''}
                            >
                              <option value="">
                                Please select required service *
                              </option>
                              {REQUIRED_SERVICE.map((service) => (
                                <option key={service} value={service}>
                                  {service}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="full-width">
                          <input
                            type="text"
                            name="how_did_you_find_us"
                            value={formData.how_did_you_find_us}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="How did you hear about us?"
                            aria-label="How did you hear about us"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="d-none">
                      <input
                        type="hidden"
                        name="service"
                        value={formData.service || ""}
                        aria-label="service"
                      />
                    </div>
                  )}

                  <div className="form-row">
                    <div className="full-width">
                      <textarea
                        rows={2}
                        className="form-input"
                        placeholder="Information on your inquiry *"
                        required
                        name="enquiry"
                        id="quote_enquiry"
                        value={formData.enquiry}
                        onChange={handleInputChange}
                        aria-label="enquiry"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="full-width marketing-opt-in">
                      <p>
                        <label>
                          <input
                            type="checkbox"
                            name="marketing"
                            checked={formData.marketing}
                            onChange={handleInputChange}
                            aria-label="marketing"
                          />
                          I agree to receive sales and marketing communication.
                          For more information about how we collect, process and
                          retain your personal data, please see our{" "}
                          <Link href="/privacy-notice" target="_blank">
                            privacy policy
                          </Link>
                          .
                        </label>
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="full-width">
                      <div className="text-center">
                        <input
                          type="hidden"
                          id="gclid_field"
                          name="WC GCLID"
                          value={formData["WC GCLID"]}
                        />
                        <div className="d-flex justify-content-center mb-3">
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
                            onChange={handleRecaptchaChange}
                          />
                        </div>
                        <button
                          type="submit"
                          className="button button-large button-primary"
                          disabled={!canSubmit()}
                          onClick={preSubmitCheck}
                          style={!canSubmit() ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                        >
                          {isSubmitting
                            ? "Submitting..."
                            : button_label || "Get my quote"}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (quote_type === "quote_grey") {
    return (
      <div
        {...storyblokEditable(blok)}
        className="quote-section-light"
        id="getQuote"
      >
        <div className="container">
          <div className="padding-small text-center">
            <h2>{title || "Become a partner"}</h2>
            <p className="larger-text">
              {description ||
                "If you're interested in our partner scheme, submit a few details below and we'll be in touch."}
            </p>

            <form className="form-quote-light" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="one-half">
                  <label htmlFor="quote_name">Name</label>
                  <input
                    className="form-input"
                    required
                    type="text"
                    name="name"
                    id="quote_name"
                    value={formData.name}
                    onChange={handleInputChange}
                    aria-label="name"
                  />
                </div>
                <div className="one-half">
                  <label htmlFor="quote_phone_number">Phone number</label>
                  <input
                    className="form-input"
                    type="tel"
                    name="phone_number"
                    id="quote_phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    aria-label="phone number"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="one-half">
                  <label htmlFor="quote_company">Company Name</label>
                  <input
                    className="form-input"
                    required
                    type="text"
                    name="company"
                    id="quote_company"
                    value={formData.company}
                    onChange={handleInputChange}
                    aria-label="company"
                  />
                </div>
                <div className="one-half">
                  <label htmlFor="quote_email_address">Email address</label>
                  <input
                    className="form-input"
                    required
                    type="email"
                    name="email_address"
                    id="quote_email_address"
                    value={formData.email_address}
                    onChange={handleInputChange}
                    aria-label="email address"
                  />
                </div>
              </div>

              {showServiceDropdown ? (
                <>
                  <div className="form-row">
                    <div className="full-width">
                      <div className="styled-select">
                        <select
                          name="service"
                          value={formData.service || ""}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          aria-label="Required service"
                          aria-required="true"
                          aria-invalid={formData.service === ''}
                        >
                          <option value="">
                            Please select required service *
                          </option>
                          {REQUIRED_SERVICE.map((service) => (
                            <option key={service} value={service}>
                              {service}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="full-width">
                      <input
                        type="text"
                        name="how_did_you_find_us"
                        value={formData.how_did_you_find_us}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="How did you hear about us?"
                        aria-label="How did you hear about us?"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="d-none">
                  <input
                    type="hidden"
                    name="service"
                    value={formData.service || ""}
                    aria-label="service"
                  />
                </div>
              )}

              <div className="form-row">
                <div className="full-width">
                  <label htmlFor="quote_enquiry">
                    Information on your inquiry
                  </label>
                  <textarea
                    rows={4}
                    className="form-input"
                    required
                    name="enquiry"
                    id="quote_enquiry"
                    value={formData.enquiry}
                    onChange={handleInputChange}
                    aria-label="enquiry"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="full-width marketing-opt-in">
                  <p>
                    <label>
                      <input
                        type="checkbox"
                        name="marketing"
                        id="quote_marketing"
                        checked={formData.marketing}
                        onChange={handleInputChange}
                        aria-label="marketing"
                      />
                      I agree to receive sales and marketing communication
                    </label>
                  </p>
                  <p>
                    By submitting this form, I agree to the Target Defense{" "}
                    <Link href="/privacy-notice" target="_blank">
                      privacy policy
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <div className="form-row">
                <div className="full-width">
                  <div className="text-center">
                    <div className="d-flex justify-content-center mb-3">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
                        onChange={handleRecaptchaChange}
                      />
                    </div>
                    <button
                      type="submit"
                      className="button button-large button-primary"
                      disabled={!canSubmit()}
                      onClick={preSubmitCheck}
                      style={!canSubmit() ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                    >
                      {isSubmitting
                        ? "Submitting..."
                        : button_label || "Get my quote"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      {...(blok && storyblokEditable(blok))}
      className="quote-section-gallery-grey"
    >
      <div className="container py-4 py-md-5" id="getQuote">
        <div className="card card-lighter my-md-4">
          <div className="row">
            {image && (
              <div className="col-lg" style={{ position: 'relative', minHeight: '300px' }}>
                <Image
                  src={image.filename as string}
                  alt=""
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  loading="lazy"
                  style={{ objectFit: 'cover' }}
                />
              </div>
            )}

            <div className="col-lg p-4 p-md-5">
              {title && (
                <h2 className="h3 text-center text-lg-start mb-4">{title}</h2>
              )}
              {description && (
                <p className="text-center text-lg-start mb-4">{description}</p>
              )}

              {errors.length > 0 && (
                <div className="d-flex justify-content-center">
                  <div className="alert alert-error w-100">
                    {errors.map((error, index) => (
                      <p key={index} className="mb-0">
                        {error.message}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {shouldShowSubmissionError && (
                <div className="d-flex justify-content-center">
                  <div className="alert alert-error w-100">
                    <p className="mb-0">{submissionError}</p>
                  </div>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit}>
                {/* Name and Phone Row */}
                <div className="form-row">
                  <div className="full-width">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Name *"
                      required
                      aria-label="Name"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="full-width">
                    <input
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Phone number"
                      aria-label="Phone number"
                    />
                  </div>
                </div>

                {/* Company Name Row */}
                <div className="form-row">
                  <div className="full-width">
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Company Name *"
                      required
                      aria-label="Company Name "
                    />
                  </div>
                </div>

                {/* Email Address Row */}
                <div className="form-row">
                  <div className="full-width">
                    <input
                      type="email"
                      name="email_address"
                      value={formData.email_address}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Email address *"
                      required
                      aria-label="Email address"
                    />
                  </div>
                </div>

                {/* Service Selection Row */}
                {showServiceDropdown ? (
                  <>
                    <div className="form-row">
                      <div className="full-width">
                        <div className="styled-select">
                          <select
                            name="service"
                            value={formData.service || ""}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                            aria-label="Required service"
                            aria-required="true"
                            aria-invalid={formData.service === ''}
                          >
                            <option value="">
                              Please select required service *
                            </option>
                            {REQUIRED_SERVICE.map((service) => (
                              <option key={service} value={service}>
                                {service}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="full-width">
                        <input
                          type="text"
                          name="how_did_you_find_us"
                          value={formData.how_did_you_find_us}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="How did you hear about us?"
                          aria-label="How did you hear about us?"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="d-none">
                    <input
                      type="hidden"
                      name="service"
                      value={formData.service || ""}
                      aria-label="service"
                    />
                  </div>
                )}

                {/* Inquiry/Comments Row */}
                <div className="form-row">
                  <div className="full-width">
                    <textarea
                      name="enquiry"
                      value={formData.enquiry}
                      onChange={handleInputChange}
                      rows={4}
                      className="form-input"
                      placeholder="Information on your inquiry *"
                      required
                      aria-label="enquiry"
                    />
                  </div>
                </div>

                {/* Marketing Opt-in Row */}
                <div className="form-row">
                  <div className="full-width marketing-opt-in">
                    <p>
                      <label>
                        <input
                          type="checkbox"
                          name="marketing"
                          checked={formData.marketing}
                          onChange={handleInputChange}
                          aria-label="marketing"
                        />
                        I agree to receive sales and marketing communication.
                        For more information about how we collect, process and
                        retain your personal data, please see our{" "}
                        <Link href="/privacy-notice" target="_blank">
                          privacy policy
                        </Link>
                        .
                      </label>
                    </p>
                  </div>
                </div>

                {/* Submit Button Row */}
                <div className="form-row">
                  <div className="full-width">
                    <div className="text-center">
                      <div className="d-flex justify-content-center mb-3">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
                          onChange={handleRecaptchaChange}
                        />
                      </div>
                      <button
                        type="submit"
                        className="button button-large button-primary"
                        disabled={!canSubmit()}
                        onClick={preSubmitCheck}
                        style={!canSubmit() ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                      >
                        {isSubmitting
                          ? "Submitting..."
                          : button_label || "Get my quote"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
